import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomButton, Loader, NavigateBack } from "../../components";
import { Notify } from "../../utils";
import { RoleService, UserService } from "../../_service";
import { InputContainer } from "./styles";
import { FormSubmitButtonContainer, LoaderContainer } from "../../CommonStyles";

interface AccessData {
  read: boolean;
  write: boolean;
  delete: boolean;
}

interface PermissionData extends AccessData {
  id: number;
  name: string;
}

interface PermissionResponse extends AccessData {
  section: {
    id: number;
    name: string;
  };
}

const EditRoles = () => {
  const { state } = useLocation();
  const [allSections, setAllSections] = useState<PermissionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const toggleAccessArrayState = (
    section_id: number,
    access: "read" | "write" | "delete",
  ) => {
    const index = allSections.findIndex((obj) => obj.id === section_id);
    const sectionData = allSections;
    sectionData[index][access] = !sectionData[index][access];
    setAllSections(() => sectionData);
  };

  const getAllSections = async () => {
    try {
      const res = await RoleService._getAllSections();
      if (res.status === 200) {
        const sectionData = res.data.map((data: PermissionData) => ({
          id: data.id,
          name: data.name,
          read: false,
          write: false,
          delete: false,
        }));

        setAllSections(() => sectionData);
      }
    } catch (e: any) {
      Notify("Some Error Occurred");
    }
  };

  const getUserPermissionDetails = async () => {
    setIsLoading(() => true);
    try {
      const res = await UserService._getUserPermissions(state.userId);
      if (res.status === 200) {
        if (res.data.length > 0) {
          const permissionData = res.data.map((data: PermissionResponse) => ({
            id: data.section.id,
            name: data.section.name,
            read: data.read,
            write: data.write,
            delete: data.delete,
          }));

          setAllSections(() => permissionData);
        } else {
          getAllSections();
        }
      }
    } catch (e: any) {
      Notify("Some Error Occurred");
    }
    setIsLoading(() => false);
  };

  useEffect(() => {
    getUserPermissionDetails();
  }, []);

  const submitRoleAccess = async (e: any) => {
    e.preventDefault();
    const dataToSend = {
      user_id: state.userId,
      management_data: allSections.map((section) => ({
        section_id: section.id,
        read: section.read,
        write: section.write,
        delete: section.delete,
      })),
    };

    try {
      const res = await UserService._updateUserPermissions(dataToSend);
      if (res.status >= 200 && res.status < 300) {
        Notify("Permissions Updated Successfully", 1);
        navigate("/dashboard/manage-users");
      }
    } catch (error) {
      Notify("Some Error Occurred");
    }
  };

  return (
    <div>
      {isLoading ? (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      ) : (
        <>
          <NavigateBack />
          <div className="title">
            <h2>
              Edit Access Level - {state?.firstName || ""}{" "}
              {state?.lastName || ""}
            </h2>
          </div>
          <div>
            <form className="role-form">
              <div className="display-flex">
                <label htmlFor="role">Role Name</label>
                <input
                  type="text"
                  name="role"
                  className="role-input"
                  value={state?.role || ""}
                  disabled
                />
              </div>
              <table style={{ width: "90%" }}>
                <thead>
                  <th
                    style={{
                      fontWeight: "bold",
                      textAlign: "left",
                    }}
                  >
                    Action
                  </th>
                  <th style={{ fontWeight: "bold" }}>Read</th>
                  <th style={{ fontWeight: "bold" }}>Add/Change</th>
                  <th style={{ fontWeight: "bold" }}>Delete</th>
                </thead>
                <tbody>
                  {allSections.map((section) => (
                    <tr key={section.name}>
                      <td style={{ marginBottom: 20 }}>{section.name}</td>
                      <td>
                        <InputContainer>
                          <input
                            type="checkbox"
                            name={`${section.name}-view`}
                            onClick={() =>
                              toggleAccessArrayState(section.id, "read")
                            }
                            defaultChecked={section.read}
                          />
                        </InputContainer>
                      </td>
                      <td>
                        <InputContainer>
                          <input
                            type="checkbox"
                            name={`${section.name}-edit`}
                            onClick={() =>
                              toggleAccessArrayState(section.id, "write")
                            }
                            defaultChecked={section.write}
                          />
                        </InputContainer>
                      </td>
                      <td>
                        <InputContainer>
                          <input
                            type="checkbox"
                            name={`${section.name}-delete`}
                            onClick={() =>
                              toggleAccessArrayState(section.id, "delete")
                            }
                            defaultChecked={section.delete}
                          />
                        </InputContainer>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <FormSubmitButtonContainer>
                <CustomButton
                  onClick={() => navigate("/dashboard/manage-users")}
                  title="Cancel"
                  type="secondary"
                />
                <CustomButton
                  onClick={submitRoleAccess}
                  title="Update Permissions"
                />
              </FormSubmitButtonContainer>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default EditRoles;
